import { useEffect, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { Alert, AuthPage, Button, FormField, Input, Spinner } from '@nexvia-logistics/ui';
import { useAuth } from '@/shared/useAuth';
import { isEmailPasswordAuthEnabled } from '@/shared/auth-options';
import { useStorageModuleState } from '@/shared/useStorageModuleState';
import StorageModuleOffPage from '@/shared/StorageModuleOffPage';
import { describeStorageProfileError, hasStorageProfile, registerStorage, type RegisterStorageInput } from '@/pages/empresa/storage-api';
import {
  FIELD_MAX_LENGTH,
  validateRegistration,
  type RegistrationErrors,
  type RegistrationInput
} from '@/pages/empresa/storage-profile-fields';

/** Same link-shaped footer button as `Login.tsx` (the design system has none — spec "Not adopted"). */
const LINK_CLASS = 'inline-flex min-h-11 cursor-pointer items-center border-0 bg-transparent p-0 text-ui-label text-primary hover:underline';

type ProfileCheck = 'checking' | 'needs-registration' | 'registered' | 'check-failed';

function CenteredSpinner({ label }: { label: string }) {
  return (
    <div className="grid min-h-dvh place-items-center bg-surface-container">
      <Spinner size={32} label={label} />
    </div>
  );
}

/** Google's mark, at Google's colours — same brand artwork as `Login.tsx`. */
const GoogleIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
    <path fill="#FBBC05" d="M5.84 14.1c-.22-.66-.35-1.36-.35-2.1s.13-1.44.35-2.1V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.83z" />
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.83c.87-2.6 3.3-4.53 6.16-4.53z" />
  </svg>
);

/** Firebase's own minimum (`auth/weak-password` below it). */
const MIN_PASSWORD_LENGTH = 6;
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * NEX-1164 — step 1 for a visitor with no session: create the login, with email + password or with
 * Google (business doc §4: the depósito has "su propia interfaz web y su login separado"). Same
 * Firebase call as nexvia-web-app's register form (`createUserWithEmailAndPassword`).
 */
function SignUpStep() {
  const { authError, signUpWithEmail, signInWithGoogle, clearAuthError } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fieldErrors, setFieldErrors] = useState<{ email?: string; password?: string }>({});
  const [submitting, setSubmitting] = useState(false);
  const [googleSubmitting, setGoogleSubmitting] = useState(false);

  const handleSubmit = async () => {
    const errors: { email?: string; password?: string } = {};
    if (!EMAIL_PATTERN.test(email.trim())) errors.email = 'Ingresá un correo válido.';
    if (password.length < MIN_PASSWORD_LENGTH) errors.password = `La contraseña tiene que tener al menos ${MIN_PASSWORD_LENGTH} caracteres.`;
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) return;

    setSubmitting(true);
    try {
      await signUpWithEmail(email, password);
      // The session flips to signed in (email not verified yet): RegistroPage shows the next step.
    } catch {
      /* authError is already set by useAuth */
    } finally {
      setSubmitting(false);
    }
  };

  const handleGoogle = async () => {
    setGoogleSubmitting(true);
    try {
      await signInWithGoogle();
    } catch {
      /* a closed popup is silent; any other failure is already in authError */
    } finally {
      setGoogleSubmitting(false);
    }
  };

  const googleButton = (
    <Button
      type="button"
      variant="neutral"
      size="xs"
      fullWidth
      showArrow={false}
      onClick={() => void handleGoogle()}
      disabled={googleSubmitting}
    >
      <GoogleIcon />
      {googleSubmitting ? 'Ingresando…' : 'Registrarte con Google'}
    </Button>
  );

  const loginLink = (
    <button
      type="button"
      onClick={() => {
        clearAuthError();
        navigate('/login');
      }}
      className={LINK_CLASS}
    >
      ¿Ya tenés cuenta? Iniciá sesión
    </button>
  );

  // Product decision 2026-10-19: Google-only sign-up. The email + password form below is hidden,
  // not removed (`shared/auth-options.ts`). With no `social` slot, `AuthPage` draws no "o" divider.
  if (!isEmailPasswordAuthEnabled()) {
    return (
      <AuthPage
        title="Registrá tu depósito"
        description="Creá tu cuenta para ofrecer tu depósito en Nexvia."
        notice={authError ? <Alert tone="critical">{authError}</Alert> : undefined}
        footer={loginLink}
      >
        {googleButton}
      </AuthPage>
    );
  }

  return (
    <AuthPage
      title="Registrá tu depósito"
      description="Creá tu cuenta para ofrecer tu depósito en Nexvia."
      notice={authError ? <Alert tone="critical">{authError}</Alert> : undefined}
      social={googleButton}
      onSubmit={() => void handleSubmit()}
      actions={
        <Button type="submit" variant="primary" size="xs" fullWidth showArrow={false} disabled={submitting}>
          {submitting ? 'Creando tu cuenta…' : 'Crear cuenta'}
        </Button>
      }
      footer={loginLink}
    >
      <FormField label="Correo electrónico" error={fieldErrors.email}>
        {(field) => (
          <Input
            {...field}
            invalid={Boolean(fieldErrors.email)}
            type="email"
            autoComplete="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            placeholder="tu@empresa.com"
          />
        )}
      </FormField>
      <FormField label="Contraseña" hint={`Al menos ${MIN_PASSWORD_LENGTH} caracteres.`} error={fieldErrors.password}>
        {(field) => (
          <Input
            {...field}
            invalid={Boolean(fieldErrors.password)}
            type="password"
            autoComplete="new-password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
          />
        )}
      </FormField>
    </AuthPage>
  );
}

/**
 * NEX-1164 — step 2 for an email + password login: nexvia-api-web refuses a new login whose email is
 * not verified (`auth.middleware.ts`, 401 `AUTH_EMAIL_NOT_VERIFIED`), so nothing reaches the API
 * (not even `GET /storage/me`) until the person clicks the link Firebase emailed.
 */
function VerifyEmailStep({ email }: { email: string | null }) {
  const { refreshEmailVerification, resendVerificationEmail, logout } = useAuth();
  const [checking, setChecking] = useState(false);
  const [notice, setNotice] = useState<{ tone: 'caution' | 'positive' | 'critical'; text: string } | null>(null);

  const handleCheck = async () => {
    setChecking(true);
    setNotice(null);
    try {
      const verified = await refreshEmailVerification();
      if (!verified) {
        setNotice({ tone: 'caution', text: 'Todavía no vemos tu correo verificado. Abrí el enlace del correo que te mandamos y probá de nuevo.' });
      }
    } catch {
      setNotice({ tone: 'critical', text: 'No pudimos revisar tu correo. Probá de nuevo en un momento.' });
    } finally {
      setChecking(false);
    }
  };

  const handleResend = async () => {
    setNotice(null);
    try {
      await resendVerificationEmail();
      setNotice({ tone: 'positive', text: 'Te mandamos el correo de nuevo.' });
    } catch {
      setNotice({ tone: 'critical', text: 'No pudimos mandar el correo. Esperá unos minutos y probá de nuevo.' });
    }
  };

  return (
    <AuthPage
      title="Verificá tu correo"
      description={
        // W2-sto review — also reached from /login by an account created elsewhere (nexvia-web-app
        // sends no verification email), so it does not claim an email was just sent.
        <>
          Para seguir, confirmá tu correo <strong className="wrap-anywhere">{email}</strong> con el enlace de verificación que te llega por correo.
          Si no lo encontrás (revisá también el spam), pedí otro con «Reenviar el correo». Después volvé acá para
          completar los datos de tu depósito.
        </>
      }
      notice={notice ? <Alert tone={notice.tone}>{notice.text}</Alert> : undefined}
      onSubmit={() => void handleCheck()}
      actions={
        <Button type="submit" variant="primary" size="xs" fullWidth showArrow={false} disabled={checking}>
          {checking ? 'Revisando…' : 'Ya verifiqué mi correo'}
        </Button>
      }
      footer={
        <span className="flex flex-wrap justify-center gap-4">
          <button type="button" onClick={() => void handleResend()} className={LINK_CLASS}>
            Reenviar el correo
          </button>
          <button type="button" onClick={() => void logout()} className={LINK_CLASS}>
            Usar otra cuenta
          </button>
        </span>
      }
    >
      {null}
    </AuthPage>
  );
}

/**
 * `/registro` — NEX-1164 "Alta y cuenta del depósito": a signed-in Firebase login with no depósito
 * account yet gives Nexvia its razón social, RUT, teléfono and the email that receives the quote
 * requests, through `POST /auth/register-storage`. NEX-1164 (W2-sto), decided — closing the open
 * question on an email sign-up (business doc §4, "su propia interfaz web y su login separado"): a
 * visitor with no session creates its login here first, with email + password (`SignUpStep`, then
 * `VerifyEmailStep` until the email is verified) or with Google. This replaces the alta
 * automática with placeholder data the Google login used to do (business doc §5: "registro con
 * RUT y razón social"). After it, the account is `incomplete` until its three documents are
 * uploaded from Mi empresa, which is where this page sends it.
 *
 * Outside the shell (`AppLayout`) on purpose: there is no account to show a panel for yet. The
 * card is the same `AuthPage` the login uses. `AuthPage` owns the `<form>` (no `noValidate`): the
 * inputs carry no `required`, so an empty submit reaches `validateRegistration` (same as Login's
 * spec D-1.1), and `type="email"` lets the browser stop a malformed contact email first.
 *
 * IVA: no field — every depósito invoices with IVA (business doc §5), stated in the RUT hint.
 *
 * NEX-1645 — with the module `off` (`GET /app/features`) neither the sign-up nor the registration
 * is offered (the API would refuse `POST /auth/register-storage` with 503 `STORAGE_MODULE_DISABLED`):
 * the page shows "todavía no está disponible" and asks the API nothing. `registration_only` and
 * `full` register as before.
 */
export default function RegistroPage() {
  const { user, loading, isAuthenticated, logout } = useAuth();
  const moduleState = useStorageModuleState();
  const moduleOpen = moduleState !== null && moduleState !== 'off';
  // NEX-1164 — nothing reaches the API before the email is verified (AUTH_EMAIL_NOT_VERIFIED).
  const readyForApi = isAuthenticated && user?.emailVerified !== false && moduleOpen;
  const navigate = useNavigate();
  const [profileCheck, setProfileCheck] = useState<ProfileCheck>('checking');
  const [form, setForm] = useState<RegistrationInput>({ razonSocial: '', rut: '', phone: '', contactEmail: '' });
  const [fieldErrors, setFieldErrors] = useState<RegistrationErrors>({});
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [checkAttempt, setCheckAttempt] = useState(0);

  // An account that already exists is never registered twice from here (e.g. a bookmarked
  // /registro): it goes straight to the panel. The form only shows once the API confirmed there is
  // no account (404 STORAGE_PROFILE_NOT_FOUND): a repeat POST /auth/register-storage on an account
  // that exists overwrites its razón social, RUT and teléfono (register-storage.ts's
  // applyRepeatCall), even a verified one, so a failed check offers a retry instead of the form.
  useEffect(() => {
    if (!readyForApi) return;
    let cancelled = false;
    hasStorageProfile()
      .then((exists) => {
        if (!cancelled) setProfileCheck(exists ? 'registered' : 'needs-registration');
      })
      .catch(() => {
        if (!cancelled) setProfileCheck('check-failed');
      });
    return () => {
      cancelled = true;
    };
  }, [readyForApi, checkAttempt]);

  // The login email is the default contact for quotes (the API's own default too); editable here.
  const loginEmail = user?.email ?? '';
  useEffect(() => {
    setForm((current) => (current.contactEmail ? current : { ...current, contactEmail: loginEmail }));
  }, [loginEmail]);

  if (loading) return <CenteredSpinner label="Cargando tu sesión…" />;
  if (moduleState === null) return <CenteredSpinner label="Cargando…" />;
  if (moduleState === 'off') {
    return <StorageModuleOffPage onSignOut={isAuthenticated ? () => void logout() : undefined} />;
  }
  if (!isAuthenticated) return <SignUpStep />;
  if (!readyForApi) return <VerifyEmailStep email={user?.email ?? null} />;
  if (profileCheck === 'checking') return <CenteredSpinner label="Revisando tu cuenta…" />;
  if (profileCheck === 'registered') return <Navigate to="/" replace />;

  const leaveFooter = (
    <button type="button" onClick={() => void logout()} className={LINK_CLASS}>
      Usar otra cuenta
    </button>
  );

  if (profileCheck === 'check-failed') {
    const retry = () => {
      setProfileCheck('checking');
      setCheckAttempt((attempt) => attempt + 1);
    };
    return (
      <AuthPage
        title="Registrá tu depósito"
        notice={<Alert tone="critical">No pudimos revisar tu cuenta. Probá de nuevo en un momento.</Alert>}
        onSubmit={retry}
        actions={
          <Button type="submit" variant="primary" size="xs" fullWidth showArrow={false}>
            Reintentar
          </Button>
        }
        footer={leaveFooter}
      >
        {null}
      </AuthPage>
    );
  }

  const setField = (field: keyof RegistrationInput) => (value: string) => {
    setForm((current) => ({ ...current, [field]: value }));
    setErrorMessage(null);
  };

  const handleSubmit = async () => {
    const errors = validateRegistration(form);
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) return;

    const contactEmail = form.contactEmail.trim();
    const input: RegisterStorageInput = {
      razonSocial: form.razonSocial.trim(),
      rut: form.rut.trim(),
      phone: form.phone.trim(),
      // Blank → left out, and the API defaults it to the login email.
      ...(contactEmail ? { contactEmail } : {})
    };

    setSubmitting(true);
    setErrorMessage(null);
    try {
      await registerStorage(input);
      navigate('/empresa', { replace: true });
    } catch (err) {
      setErrorMessage(describeStorageProfileError(err, 'No pudimos registrar tu depósito. Probá de nuevo en un momento.'));
      setSubmitting(false);
    }
  };

  return (
    <AuthPage
      title="Registrá tu depósito"
      description="Con estos datos Nexvia revisa tu alta, igual que la de los transportistas. Después subís la documentación desde Mi empresa."
      notice={errorMessage ? <Alert tone="critical">{errorMessage}</Alert> : undefined}
      onSubmit={() => void handleSubmit()}
      actions={
        <Button type="submit" variant="primary" size="xs" fullWidth showArrow={false} disabled={submitting}>
          {submitting ? 'Creando tu cuenta…' : 'Crear cuenta'}
        </Button>
      }
      footer={leaveFooter}
    >
      <FormField label="Razón social" error={fieldErrors.razonSocial}>
        {(field) => (
          <Input
            {...field}
            invalid={Boolean(fieldErrors.razonSocial)}
            autoComplete="organization"
            maxLength={FIELD_MAX_LENGTH.razonSocial}
            value={form.razonSocial}
            onChange={(event) => setField('razonSocial')(event.target.value)}
          />
        )}
      </FormField>
      <FormField
        label="RUT"
        hint="12 dígitos, sin puntos ni guiones. Todos los depósitos facturan con IVA."
        error={fieldErrors.rut}
      >
        {(field) => (
          <Input
            {...field}
            invalid={Boolean(fieldErrors.rut)}
            inputMode="numeric"
            value={form.rut}
            onChange={(event) => setField('rut')(event.target.value)}
          />
        )}
      </FormField>
      <FormField label="Teléfono" error={fieldErrors.phone}>
        {(field) => (
          <Input
            {...field}
            invalid={Boolean(fieldErrors.phone)}
            type="tel"
            autoComplete="tel"
            maxLength={FIELD_MAX_LENGTH.phone}
            value={form.phone}
            onChange={(event) => setField('phone')(event.target.value)}
          />
        )}
      </FormField>
      <FormField
        label="Correo para cotizaciones"
        hint="Ahí te avisamos de las solicitudes nuevas en tu zona. Si lo dejás vacío, usamos tu correo de ingreso."
        error={fieldErrors.contactEmail}
      >
        {(field) => (
          <Input
            {...field}
            invalid={Boolean(fieldErrors.contactEmail)}
            type="email"
            autoComplete="email"
            maxLength={FIELD_MAX_LENGTH.contactEmail}
            value={form.contactEmail}
            onChange={(event) => setField('contactEmail')(event.target.value)}
          />
        )}
      </FormField>
    </AuthPage>
  );
}
